import { EyeOutlined, FormOutlined, MessageOutlined } from '@ant-design/icons';
import { Button, Space, Tag, Tooltip, message } from 'antd';
import moment from 'moment';
import React, { useContext, useEffect, useRef, useState } from 'react';
import { useGoogleReCaptcha } from 'react-google-recaptcha-v3';
import { useHistory } from 'react-router-dom';
import CustomCopyable from '../../components/CustomCopyable';
import ResponsiveContainer from '../../components/ResponsiveContainer';
import TableAndFilters from '../../components/TableAndFilters';
import UserContext from '../../context/user-context';
import {
  ESTADOS_SINIESTRO,
  ETAPAS_SINIESTRO,
  RESULTADOS_SINIESTRO,
  toUpperCase
} from '../../utils';
import { instanceRef } from '../../utils/axios';
import { fieldTypes } from '../../utils/filter-utils';

function SiniestroList() {
  const { user } = useContext(UserContext);
  const [columns, setColumns] = useState([]);
  const { executeRecaptcha } = useGoogleReCaptcha();
  const history = useHistory();

  const tableRef = useRef();

  const getTooltip = (etapa, text) => {
    if (etapa === 'PENDIENTE') {
      return `${text} para completar datos`;
    }
    if (etapa === 'PENDIENTE_FIRMA') {
      return `${text} para firma`;
    }
    if (etapa === 'FIRMADA') {
      return `${text} para enviar fotos`;
    }
    if (etapa === 'AMPLIACION_PENDIENTE') {
      return `${text} para flujo de ampliación`;
    }
    if (etapa === 'FINALIZADA') {
      return `${text}`;
    }
    if (etapa === 'INICIADA') {
      return `${text}`;
    }
    return '';
  };

  const getLink = (record) => {
    if (record.etapa === 'PENDIENTE') {
      return `/denuncia-siniestro-landing/${record.token}`;
    }
    if (record.etapa === 'PENDIENTE_FIRMA') {
      return `/firmar-denuncia/${record.token}`;
    }
    if (record.etapa === 'FIRMADA') {
      return `/siniestros/form/${record.token}`;
    }
    if (record.etapa === 'INICIADA') {
      return `/siniestros/form/${record.token}`;
    }
    return '';
  };

  const getPolizaNumero = (poliza) => {
    return poliza?.numero
      ? `${poliza.seccion || ''}-${poliza.numero || ''}-${
          poliza.endoso === null ? '' : poliza.endoso
        }`
      : 'SIN NÚMERO';
  };

  const handleOpenUrl = (url) => {
    history.push(url);
  };

  const columnasComunes = [
    {
      title: 'Nro Siniestro',
      dataIndex: ['nro_siniestro']
    },
    {
      title: 'Nro Póliza',
      filterType: fieldTypes.STRING,
      sortable: true,
      render: (record) => getPolizaNumero(record.poliza)
    },
    {
      title: 'Empresa',
      dataIndex: [
        'poliza',
        'solicitud',
        'cotizacion',
        'aseguradoraEmpresa',
        'empresa',
        'nombre'
      ],
      filterType: fieldTypes.STRING,
      sortable: true
    },
    {
      title: 'Aseguradora',
      dataIndex: [
        'poliza',
        'solicitud',
        'cotizacion',
        'aseguradoraEmpresa',
        'aseguradora',
        'desc'
      ],
      filterType: fieldTypes.STRING,
      sortable: true
    },
    {
      title: 'Agente/Corredora',
      dataIndex: [
        'poliza',
        'aseguradoraEmpresa',
        'empresa',
        'corredora',
        'nombre'
      ],
      filterType: fieldTypes.STRING
    },
    {
      title: 'Cliente',
      dataIndex: ['poliza', 'solicitud', 'cotizacion', 'persona', 'nombres'],
      filterType: fieldTypes.STRING,
      sortable: true,
      width: '20%'
    },
    {
      title: 'Producto',
      dataIndex: ['poliza', 'producto', 'descripcion'],
      filterType: fieldTypes.STRING,
      sortable: true,
      width: '20%'
    },
    {
      title: 'Cédula',
      dataIndex: ['poliza', 'solicitud', 'cotizacion', 'persona', 'cedula'],
      filterType: fieldTypes.STRING,
      sortable: true,
      width: '15%'
    },
    {
      title: 'Descripción',
      dataIndex: '',
      filterType: fieldTypes.STRING,
      width: '15%',
      render: (record) => renderDescripcion(record.poliza)
    },
    {
      title: 'Fecha Siniestro',
      dataIndex: 'fecha_siniestro',
      sortable: true,
      width: '20%',
      render: (_, record) => {
        return record.fecha_siniestro
          ? moment(record.fecha_siniestro).format('DD/MM/YYYY HH:mm')
          : 'Sin fecha';
      }
    },
    {
      title: 'Fecha Denuncia',
      dataIndex: 'fecha_denuncia',
      filterType: fieldTypes.DATETIME,
      sortable: true,
      width: '15%',
      render: (_, record) => {
        return record.fecha_denuncia
          ? moment(record.fecha_denuncia).format('DD/MM/YYYY HH:mm')
          : 'Sin fecha';
      }
    },
    {
      title: 'Fecha Documentos', // TODO definir como usar
      width: '15%'
    },
    {
      title: 'Fecha Informe', // TODO definir como usar
      width: '15%'
    },
    {
      title: 'Estado',
      dataIndex: 'estado',
      render: (estado) => {
        const estadoUpperCase = toUpperCase(estado);
        let color = 'white';
        if (estadoUpperCase === 'DENUNCIA') {
          color = 'orange';
        } else if (estadoUpperCase === 'SINIESTRO') {
          color = 'blue';
        }
        return (
          <Tag color={color} key={estadoUpperCase}>
            {estadoUpperCase}
          </Tag>
        );
      },
      filterType: fieldTypes.ENUM,
      filters: Object.keys(ESTADOS_SINIESTRO).map((key) => ({
        text: ESTADOS_SINIESTRO[key],
        value: key
      })),
      sortable: true
    },
    {
      title: 'Etapa',
      dataIndex: 'etapa',
      render: (etapa) => {
        const etapaUpperCase = toUpperCase(etapa);
        let color = 'white';
        if (etapaUpperCase === 'PENDIENTE') {
          color = 'grey';
        } else if (etapaUpperCase === 'PENDIENTE_FIRMA') {
          color = 'yellow';
        } else if (
          etapaUpperCase === 'FIRMADA' ||
          etapaUpperCase === 'FOTOS_SUBIDAS'
        ) {
          color = 'blue';
        } else if (
          etapaUpperCase === 'AMPLIACION_PENDIENTE' ||
          etapaUpperCase === 'AMPLIACION_FINALIZADA'
        ) {
          color = 'orange';
        } else if (etapaUpperCase === 'FINALIZADA') {
          color = 'green';
        } else if (etapaUpperCase === 'INICIADA') {
          color = 'green';
        }
        return (
          <Tag color={color} key={etapaUpperCase}>
            {etapaUpperCase}
          </Tag>
        );
      },
      filterType: fieldTypes.ENUM,
      filters: Object.keys(ETAPAS_SINIESTRO).map((key) => ({
        text: ETAPAS_SINIESTRO[key],
        value: key
      })),
      sortable: true
    },
    {
      title: 'Resultado',
      dataIndex: 'resultado',
      render: (resultado) => {
        const resultadoUpperCase = toUpperCase(resultado);
        let color = 'white';
        if (resultadoUpperCase === 'PENDIENTE') {
          color = 'grey';
        } else if (
          resultadoUpperCase === 'APROBADO' ||
          resultadoUpperCase === 'ORDENES'
        ) {
          color = 'blue';
        } else if (resultadoUpperCase === 'RECHAZADO') {
          color = 'orange';
        } else if (resultadoUpperCase === 'FINIQUITADO') {
          color = 'green';
        }
        return (
          <Tag color={color} key={resultadoUpperCase}>
            {resultadoUpperCase}
          </Tag>
        );
      },
      filterType: fieldTypes.ENUM,
      filters: Object.keys(RESULTADOS_SINIESTRO).map((key) => ({
        text: RESULTADOS_SINIESTRO[key],
        value: key
      })),
      sortable: true,
      width: '20%'
    }
  ];

  useEffect(() => {
    let columnas = [];
    if (
      user &&
      (user['cognito:groups'][0] === 'ADMIN_ASEGURADORA' ||
        user['cognito:groups'][0] === 'ADMIN' ||
        user['cognito:groups'][0] === 'ADMIN_EMPRESA' ||
        user['cognito:groups'][0] === 'ADMIN_SUCURSAL')
    ) {
      columnas = [
        ...columnasComunes,
        {
          title: 'Acciones',
          key: 'action',
          align: 'left',
          width: '10px',
          render: (text, record) => (
            <Space size="small">
              <div>
                <Tooltip title="Consultar">
                  <Button
                    type="text"
                    icon={<EyeOutlined />}
                    size="middle"
                    onClick={() => handleOpenUrl(`siniestros/${record.token}`)}
                  />
                </Tooltip>
              </div>
              <div
                style={{
                  visibility:
                    record.etapa === 'FOTOS_SUBIDAS' ||
                    record.etapa === 'AMPLIACION_FINALIZADA' ||
                    record.etapa === 'FINALIZADA'
                      ? 'hidden'
                      : ''
                }}
              >
                <Tooltip title="Continuar Siniestro">
                  <Button
                    type="text"
                    icon={<FormOutlined />}
                    size="middle"
                    onClick={() => handleOpenUrl(getLink(record))}
                  />
                </Tooltip>
              </div>
              <Tooltip
                title={getTooltip(record.estado, 'Enviar enlace por SMS')}
              >
                <Button
                  type="text"
                  shape="circle"
                  onClick={() => {
                    sendSms(record);
                  }}
                  icon={<MessageOutlined />}
                  size="middle"
                />
              </Tooltip>
              <Button
                type="text"
                icon={
                  <CustomCopyable
                    copyable={{
                      text: getLink(record),
                      tooltips: record.ampliacion
                        ? [
                            'Copiar enlace para subir fotos de ampliación',
                            'Copiado'
                          ]
                        : [
                            getTooltip(record.estado, 'Copiar enlace'),
                            'Copiado'
                          ]
                    }}
                  />
                }
                size="middle"
              />
            </Space>
          )
        }
      ];
    } else if (user && user['cognito:groups'][0] === 'EJECUTIVO') {
      columnas = [
        ...columnasComunes,
        {
          title: 'Acciones',
          key: 'action',
          align: 'left',
          width: '10px',
          render: (text, record) => (
            <Space size="small">
              <div
                style={{
                  visibility:
                    record.etapa === 'FOTOS_SUBIDAS' ||
                    record.etapa === 'AMPLIACION_FINALIZADA' ||
                    record.etapa === 'FINALIZADA'
                      ? ''
                      : 'hidden'
                }}
              >
                <Tooltip title="Consultar">
                  <Button
                    type="text"
                    icon={<EyeOutlined />}
                    size="middle"
                    onClick={() => handleOpenUrl(`siniestros/${record.token}`)}
                  />
                </Tooltip>
              </div>
              <div
                style={{
                  visibility:
                    record.etapa === 'FOTOS_SUBIDAS' ||
                    record.etapa === 'AMPLIACION_FINALIZADA' ||
                    record.etapa === 'FINALIZADA'
                      ? 'hidden'
                      : ''
                }}
              >
                <Tooltip title="Continuar Siniestro">
                  <Button
                    type="text"
                    icon={<FormOutlined />}
                    size="middle"
                    onClick={() => handleOpenUrl(getLink(record))}
                  />
                </Tooltip>
              </div>
              <Tooltip
                title={getTooltip(record.estado, 'Enviar enlace por SMS')}
              >
                <Button
                  type="text"
                  shape="circle"
                  onClick={() => {
                    sendSms(record);
                  }}
                  icon={<MessageOutlined />}
                  size="middle"
                />
              </Tooltip>
              <Button
                type="text"
                icon={
                  <CustomCopyable
                    copyable={{
                      text: getLink(record),
                      tooltips: record.ampliacion
                        ? [
                            'Copiar enlace para subir fotos de ampliación',
                            'Copiado'
                          ]
                        : [
                            getTooltip(record.estado, 'Copiar enlace'),
                            'Copiado'
                          ]
                    }}
                  />
                }
                size="middle"
              />
            </Space>
          )
        }
      ];
    } else if (user && user['cognito:groups'][0] === 'CLIENTE') {
      columnas = [
        ...columnasComunes,
        {
          title: 'Acciones',
          key: 'action',
          align: 'left',
          width: '10px',
          render: (text, record) => (
            <Space size="small">
              <div
                style={{
                  visibility:
                    record.etapa === 'FOTOS_SUBIDAS' ||
                    record.etapa === 'AMPLIACION_FINALIZADA' ||
                    record.etapa === 'FINALIZADA'
                      ? ''
                      : 'hidden'
                }}
              >
                <Tooltip title="Consultar">
                  <Button
                    type="text"
                    icon={<EyeOutlined />}
                    size="middle"
                    onClick={() => handleOpenUrl(`siniestros/${record.token}`)}
                  />
                </Tooltip>
              </div>
              <div
                style={{
                  visibility:
                    record.etapa === 'FOTOS_SUBIDAS' ||
                    record.etapa === 'AMPLIACION_FINALIZADA' ||
                    record.etapa === 'FINALIZADA'
                      ? 'hidden'
                      : ''
                }}
              >
                <Tooltip title="Continuar Siniestro">
                  <Button
                    type="text"
                    icon={<FormOutlined />}
                    size="middle"
                    onClick={() => handleOpenUrl(getLink(record))}
                  />
                </Tooltip>
              </div>
            </Space>
          )
        }
      ];
    } else if (user && user['cognito:groups'][0] === 'PERITO') {
      columnas = [
        ...columnasComunes,
        {
          title: 'Acciones',
          key: 'action',
          align: 'left',
          width: '10px',
          render: (text, record) => (
            <Space size="small">
              <div
                style={{
                  visibility:
                    record.etapa !== 'FOTOS_SUBIDAS' &&
                    record.etapa !== 'AMPLIACION_FINALIZADA'
                      ? ''
                      : 'hidden'
                }}
              >
                <Tooltip title="Consultar">
                  <Button
                    type="text"
                    icon={<EyeOutlined />}
                    size="middle"
                    onClick={() => handleOpenUrl(`siniestros/${record.token}`)}
                  />
                </Tooltip>
              </div>
              <div
                style={{
                  visibility:
                    record.etapa === 'FOTOS_SUBIDAS' ||
                    record.etapa === 'AMPLIACION_FINALIZADA'
                      ? ''
                      : 'hidden'
                }}
              >
                <Tooltip title="Editar">
                  <Button
                    type="text"
                    icon={<FormOutlined />}
                    size="middle"
                    onClick={() => handleOpenUrl(getLink(record))}
                  />
                </Tooltip>
              </div>
              <div
                style={{
                  visibility:
                    record.etapa !== 'FOTOS_SUBIDAS' &&
                    record.etapa !== 'AMPLIACION_FINALIZADA'
                      ? ''
                      : 'hidden'
                }}
              >
                {' '}
                <Tooltip
                  title={getTooltip(record.estado, 'Enviar enlace por SMS')}
                >
                  <Button
                    type="text"
                    shape="circle"
                    onClick={() => {
                      sendSms(record);
                    }}
                    icon={<MessageOutlined />}
                    size="middle"
                  />
                </Tooltip>
              </div>
              <div
                style={{
                  visibility:
                    record.etapa !== 'FOTOS_SUBIDAS' &&
                    record.etapa !== 'AMPLIACION_FINALIZADA'
                      ? ''
                      : 'hidden'
                }}
              >
                <Button
                  type="text"
                  icon={
                    <CustomCopyable
                      copyable={{
                        text: getLink(record),
                        tooltips: record.ampliacion
                          ? [
                              'Copiar enlace para subir fotos de ampliación',
                              'Copiado'
                            ]
                          : [
                              getTooltip(record.estado, 'Copiar enlace'),
                              'Copiado'
                            ]
                      }}
                    />
                  }
                  size="middle"
                  disabled={
                    record.etapa === 'FOTOS_SUBIDAS' ||
                    record.etapa === 'AMPLIACION_FINALIZADA'
                  }
                />
              </div>
            </Space>
          )
        }
      ];
    }
    setColumns(columnas);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]);

  const renderDescripcion = (record) => {
    if (record.producto.codigo === 1 || record.producto.codigo === 7) {
      return `${record.vehiculo.marca.descripcion}
            - ${record.vehiculo.modelo.descripcion} -
            ${
              record.vehiculo.aniosubmodelo
                ? record.vehiculo.aniosubmodelo.descripcion
                : ''
            } -
            ${record.vehiculo.chapa ? record.vehiculo.chapa : ''}`;
      // eslint-disable-next-line no-else-return
    } else if (
      record.producto.codigo === 4 ||
      record.producto.codigo === 5 ||
      record.producto.codigo === 6
    ) {
      return `${record.solicitud.inmueble.domicilio}
      - ${record.solicitud.inmueble.departamento.descripcion}`;
    }
    return `${
      record.solicitud.asegurados
        ? record.solicitud.asegurados[0].nombres
        : record.persona.nombres
    }`;
  };

  const sendSms = async (record) => {
    try {
      const data = {
        persona: {
          celular: record.poliza.solicitud.cotizacion.persona.celular
        },
        token: record.token,
        etapa: record.etapa,
        idEmpresa:
          record.poliza.solicitud.cotizacion.aseguradoraEmpresa.id_empresa
      };
      if (record.etapa === 'PENDIENTE') {
        message.info(
          `${record.poliza.solicitud.cotizacion.persona.celular} recibirá un mensaje de texto para completar los datos`
        );
      } else if (record.etapa === 'PENDIENTE_FIRMA') {
        message.info(
          `${record.poliza.solicitud.cotizacion.persona.celular} recibirá un mensaje de texto para firmar documento`
        );
      } else if (record.etapa === 'FIRMADA') {
        message.info(
          `${record.poliza.solicitud.cotizacion.persona.celular} recibirá un mensaje de texto para subir fotos`
        );
      } else message.info(``);
      const reCaptchaToken = await executeRecaptcha('sms/siniestro');
      data.reCaptchaToken = reCaptchaToken;
      await instanceRef.instance.post(`sms/siniestro`, data);
    } catch (error) {
      // error
    }
  };

  return (
    <ResponsiveContainer size="large" style={{ paddingTop: 0 }}>
      <TableAndFilters
        withGeneralFilter
        ref={tableRef}
        url="/siniestro"
        title="Siniestros"
        columns={columns}
        showTable
        hasExportar={false}
      />
    </ResponsiveContainer>
  );
}

export default SiniestroList;
